import { useState, useEffect, useMemo } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import MDBox from "components/MDBox";
import Sidenav from "examples/Sidenav";
import ProtectedRoute from "components/ProtectedRoute";
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import routes from "routes";
import { getRoutesForUser } from "routes/roleBasedRoutes";
import { useMaterialUIController, setMiniSidenav } from "context";
// Use custom logo from public folder
import Profile from "layouts/profile";
import SignIn from "layouts/authentication/sign-in";
import Frontpage from "page/Frontpage";
import GlobalSnackbarHost from "components/GlobalSnackbarHost";
// New: Global providers for improved UX
import { SnackbarProvider } from "components/GlobalSnackbar";
import { ConfirmDialogProvider } from "components/ConfirmDialog";

export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
    user,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();

  // Get routes based on current user
  const userRoutes = useMemo(() => {
    return getRoutesForUser(user);
  }, [user]);

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });
    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  // Flatten and render all routes
  function getRoutes(allRoutes) {
    let routesArr = [];
    allRoutes.forEach((route) => {
      if (route.collapse) {
        routesArr = routesArr.concat(getRoutes(route.collapse));
      } else if (route.route && route.component) {
        // Wrap protected routes
        if (route.protected !== false) {
          routesArr.push(
            <Route
              path={route.route}
              element={<ProtectedRoute>{route.component}</ProtectedRoute>}
              key={route.key}
            />
          );
        } else {
          routesArr.push(<Route path={route.route} element={route.component} key={route.key} />);
        }
      }
    });
    return routesArr;
  }

  // RTL support
  if (direction === "rtl") {
    return (
      <SnackbarProvider>
        <ConfirmDialogProvider>
          <CacheProvider value={rtlCache}>
            <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
              <CssBaseline />
              {layout === "dashboard" && (
                <>
                  <Sidenav
                    color={sidenavColor}
                    brand="/LogoTR.png"
                    brandName="stail"
                    routes={userRoutes}
                    onMouseEnter={handleOnMouseEnter}
                    onMouseLeave={handleOnMouseLeave}
                  />
                  <GlobalSnackbarHost />
                  <GlobalSnackbarHost />
                </>
              )}
          <Routes>
            {getRoutes(routes)}
            <Route
              path="/profile/:nama"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />
            <Route path="/authentication/sign-in" element={<SignIn />} />
            <Route
              path="*"
              element={
                user ? (
                  (() => {
                    const id = (user.id_pengguna || "").toLowerCase();
                    const role = (user.peranan || user.role || "").toLowerCase();
                    const dept = (user.bahagian || "").toLowerCase();
                    if (role === "admin" || id === "admin") return <Navigate to="/admin" />;
                    if (id === "huda") return <Navigate to="/dashboard" />;
                    if (id === "uuu" || dept === "uuu") return <Navigate to="/notis/tunggakan" />;
                    return <Navigate to="/dashboard" />;
                  })()
                ) : (
                  <Navigate to="/authentication/sign-in" />
                )
              }
            />
          </Routes>
            </ThemeProvider>
          </CacheProvider>
        </ConfirmDialogProvider>
      </SnackbarProvider>
    );
  }

  // LTR support
  return (
    <SnackbarProvider>
      <ConfirmDialogProvider>
        <ThemeProvider theme={darkMode ? themeDark : theme}>
          <CssBaseline />
  {layout === "dashboard" && user && pathname !== "/" && pathname !== "/authentication/sign-in" && (
              <>
                <Sidenav
                  color={sidenavColor}
                  brand="/LogoTR.png"
                  brandName="stail"
                  routes={userRoutes}
                  onMouseEnter={handleOnMouseEnter}
                  onMouseLeave={handleOnMouseLeave}
                />
        <GlobalSnackbarHost />
              </>
            )}
      <Routes>
        <Route path="/" element={<Frontpage />} />
        {getRoutes(routes)}
        <Route
          path="/profile/:nama"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route path="/authentication/sign-in" element={<SignIn />} />
      </Routes>
        </ThemeProvider>
      </ConfirmDialogProvider>
    </SnackbarProvider>
  );
}
